<template>
  <fts-client-modal
    :append-to-body="appendToBody"
    :width="'90%'"
    dynamic-screen
    :footer="false"
    title="Story"
    ref="traceModal"
  >
    <!-- body -->
    <template #body>
      <div v-if="response && response.data" class="trace-modal">
        <div class="trace-header">
          <div class="trace-header-row">
            <h2 class="trace-workspace-title">{{ response.data.workspace }}</h2>
          </div>

          <div
            v-if="response.data.request.traces.some((x) => x)"
            class="trace-header-row trace-meta"
          >
            <span class="trace-meta-item">
              <span class="trace-meta-label">When</span>
              {{ moment(response.data.request.traces[0].start).format("LLLL") }}
            </span>
            <span class="trace-meta-divider">·</span>
            <span class="trace-meta-item">
              <span class="trace-meta-label">By</span>
              {{ response.data.request.traces[0].by }}
            </span>
            <span class="trace-header-actions">
              <button type="button" class="trace-action-btn" @click="expandAll">Expand all</button>
              <button type="button" class="trace-action-btn" @click="collapseAll">Collapse all</button>
            </span>
          </div>
        </div>

        <div class="trace-timeline">
          <AutomationRunTrace
            v-for="(event, idx) in eventGroups"
            :key="event.id || idx"
            :history-id="historyId"
            :event="event"
          ></AutomationRunTrace>
        </div>
      </div>
    </template>
  </fts-client-modal>
</template>

<script>
import moment from "moment";
import { clientStore } from "../../store/clientStore";

import FTSClientModal from "../../elements/FTSClientModal";
import AutomationRunTrace from "../../admin/automationRunTrace";

function buildEventGroups(traces) {
  const groups = [];
  const pending = [];

  for (const trace of traces || []) {
    if (trace.type === 1) {
      const event = {
        id: trace.id,
        kind: 'event',
        activity: trace,
        befores: [],
        afters: [],
        nested: []
      };

      for (let i = pending.length - 1; i >= 0; i--) {
        const b = pending[i];
        if (b.data.moduleId === trace.data.moduleId) {
          event.befores.unshift({
            trace: b,
            nested: buildEventGroups(b.children || [])
          });
          pending.splice(i, 1);
        }
      }

      for (const child of trace.children || []) {
        const sameEventAfter =
          child.type === 0 &&
          child.data.before === false &&
          child.data.moduleId === trace.data.moduleId &&
          child.data.entryId === trace.data.entryId;

        if (sameEventAfter) {
          event.afters.push({
            trace: child,
            nested: buildEventGroups(child.children || [])
          });
        } else {
          event.nested.push(...buildEventGroups([child]));
        }
      }

      groups.push(event);
    } else if (trace.type === 0 && trace.data.before === true) {
      pending.push(trace);
    } else {
      groups.push({
        id: trace.id,
        kind: 'standalone',
        activity: trace,
        befores: [],
        afters: [],
        nested: buildEventGroups(trace.children || [])
      });
    }
  }

  for (const b of pending) {
    groups.push({
      id: b.id,
      kind: 'standalone',
      activity: b,
      befores: [],
      afters: [],
      nested: buildEventGroups(b.children || [])
    });
  }

  return groups;
}

export default {
  components: {
    "fts-client-modal": FTSClientModal,
    AutomationRunTrace
  },
  props: {
    appendToBody: Boolean,
    requestId: String,
    historyId: String,
  },
  data: () => ({
    resolve: null,
    response: {},
    traceControl: { expandAllAt: 0, collapseAllAt: 0 }
  }),
  provide() {
    return { traceControl: this.traceControl };
  },
  computed: {
    eventGroups() {
      if (!this.response || !this.response.data) return [];
      return buildEventGroups(this.response.data.request.traces);
    }
  },
  methods: {
    moment,
    expandAll() {
      this.traceControl.expandAllAt = Date.now();
    },
    collapseAll() {
      this.traceControl.collapseAllAt = Date.now();
    },
    async show() {
      try {
        this.response = await clientStore.dispatch("trace", this.requestId);
        return new Promise((resolve) => {
          this.resolve = resolve;
          this.$refs.traceModal.show();
        });
      } catch (err) {
        this.$toasted.global.error(err);
      }
    }
  }
};
</script>

<style scoped>
.trace-modal {
  color: var(--inistate-text-primary);
  font-size: var(--inistate-label-text-12);
  line-height: var(--inistate-label-text-12--line-height);
}

.trace-header {
  display: flex;
  flex-direction: column;
  gap: var(--inistate-spacing-4);
  padding: 0 var(--inistate-spacing-8) var(--inistate-spacing-12);
  margin-bottom: var(--inistate-spacing-8);
  border-bottom: 1px solid var(--inistate-color-neutral-200);
}

.trace-header-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--inistate-spacing-8);
}

.trace-workspace-title {
  margin: 0;
  font-size: var(--inistate-heading-text-16);
  line-height: var(--inistate-heading-text-16--line-height);
  font-weight: var(--inistate-font-weight-bold);
  color: var(--inistate-color-neutral-900);
}

.trace-meta {
  font-size: var(--inistate-label-text-12);
  color: var(--inistate-text-secondary);
}

.trace-meta-item {
  display: inline-flex;
  align-items: center;
  gap: var(--inistate-spacing-4);
}

.trace-meta-label {
  color: var(--inistate-color-neutral-400);
  font-weight: var(--inistate-font-weight-medium);
  text-transform: uppercase;
  font-size: var(--inistate-label-text-10);
  letter-spacing: 0.04em;
}

.trace-meta-divider {
  color: var(--inistate-color-neutral-300);
}

.trace-header-actions {
  margin-left: auto;
  display: inline-flex;
  gap: var(--inistate-spacing-4);
}

.trace-action-btn {
  padding: var(--inistate-spacing-2) var(--inistate-spacing-8);
  background: var(--inistate-color-shade-white);
  border: 1px solid var(--inistate-color-neutral-200);
  border-radius: var(--inistate-rounded-4);
  font-size: var(--inistate-label-text-12);
  line-height: var(--inistate-label-text-12--line-height);
  color: var(--inistate-text-primary);
  cursor: pointer;
  transition: background-color 0.1s ease, border-color 0.1s ease;
}

.trace-action-btn:hover {
  background: var(--inistate-color-neutral-50);
  border-color: var(--inistate-color-neutral-300);
}

.trace-timeline {
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: var(--inistate-spacing-12);
  row-gap: var(--inistate-spacing-8);
  align-items: start;
}
</style>
